/**
 * shogun-buy-now.js
 * Single-tap "Buy Now" handler: cart に追加 → 即チェックアウト遷移
 *
 * Pattern: 既存 onclick インラインを排除し、data-shogun-buy-now 属性 + AbortController で管理
 *
 * 再入ガード (CHG-079, home-floating-buy CHG-078 と同方針): fetch 完了前の連打で
 * POST /cart/add.js が複数回走り数量が増えたままチェックアウトに飛ぶのを防ぐ。
 * 要素は <a> のため disabled プロパティは効かない — data-sbn-sent 属性で判定し、
 * aria-disabled で shogun-buy-now.css の disabled スタイル (pointer-events:none) を発動。
 * キーボード操作は pointer-events で止まらないため属性判定が本体。
 * 解除は bfcache 復帰 (pageshow) と 5 秒失効タイマー (遷移が起きなかった場合の永久 disable 防止)。
 *
 * Loaded via {{ 'shogun-buy-now.js' | asset_url | script_tag }} (defer 推奨)
 */

(function() {
  'use strict';

  let _controller = null;
  const RESET_MS = 5000;

  function init() {
    // SPA 遷移で前回のリスナーを cleanup
    if (_controller) { _controller.abort(); _controller = null; }
    _controller = new AbortController();
    const { signal } = _controller;

    document.querySelectorAll('[data-shogun-buy-now]').forEach((el) => {
      el.addEventListener('click', handleClick, { signal });
    });
  }

  // 2026-09-24 Clarity: 遷移 (cart/add → /checkout → shop.app → checkouts) の間ボタンが半透明になるだけで
  // 無反応に見え、5 秒ガード中の連打が全部無視されていた → 押した直後に文言を変えて「進んでいる」を見せる
  const BUSY_LABEL = 'Going to checkout…';

  // PDP の buy 列では数量ステッパー / 3 袋カードで選んだ数量を使う (旧: 常に 1 袋)。それ以外 (TOP 等) は 1
  function selectedQty(el) {
    if (!el.closest('.product__info-container')) return 1;
    const input = document.querySelector('.product__info-container .product-form__quantity input[name="quantity"]');
    const q = parseInt(input && input.value, 10);
    return q > 0 ? q : 1;
  }

  function resetEl(el) {
    const label = el.querySelector('.shogun-buy-now__label');
    if (label && label.dataset.sbnLabel) { label.textContent = label.dataset.sbnLabel; delete label.dataset.sbnLabel; }
    el.removeAttribute('data-sbn-sent');
    el.removeAttribute('aria-disabled');
    el.removeAttribute('aria-busy');
  }

  async function handleClick(event) {
    const el = event.currentTarget;
    const variantId = el.getAttribute('data-variant-id');
    if (!variantId) return; // fall through to <a href> default behavior

    event.preventDefault();

    if (el.getAttribute('data-sbn-sent') === '1') return;
    el.setAttribute('data-sbn-sent', '1');
    el.setAttribute('aria-disabled', 'true');
    el.setAttribute('aria-busy', 'true');
    const label = el.querySelector('.shogun-buy-now__label');
    if (label) { label.dataset.sbnLabel = label.textContent; label.textContent = BUSY_LABEL; }
    // 失効タイマーは cart/add が返るまでの保険。遷移を始めたら解除しない (/review: 遅い回線で 5 秒後に再押下でき 3 袋が二重に入った)
    const resetTimer = setTimeout(function() { resetEl(el); }, RESET_MS);
    const qty = selectedQty(el);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] })
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error('cart/add failed: ' + res.status + ' ' + text);
      }
      clearTimeout(resetTimer);
      window.location.href = '/checkout';
    } catch (err) {
      // Fallback: cart permalink へ遷移 (ガードは失効タイマー / pageshow が解除する)
      console.error('[shogun-buy-now] error, falling back to /cart:', err);
      window.location.href = '/cart/' + variantId + ':' + qty;
    }
  }

  // Initial mount
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init for SPA-like navigation (Shopify Section Rendering API / theme editor)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);

  // bfcache 復帰 (checkout から戻る等) でガードを解除し再購入可能にする
  window.addEventListener('pageshow', function() {
    document.querySelectorAll('[data-shogun-buy-now][data-sbn-sent]').forEach(resetEl);
  });
})();
